import * as React from 'react';
import type {
	FieldValues,
	UseControllerProps,
	ControllerRenderProps,
	FieldPath,
} from 'react-hook-form';
import { useController } from 'react-hook-form';

import { isErrorType, type CellError } from 'af-components/RHFTableCellComponents/error.types';

export type ValueRenderMethodParams<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TValue = unknown
> = {
	field: ControllerRenderProps<TFieldValues, TName>;
	onFieldChange: (value: TValue) => void;
	onFieldBlur: () => void;
	error?: Nullable<CellError>;
};

export type RHFValueFieldWrapperProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues,
	TValue = unknown
> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
	onChange?: (name: string) => void;
	onBlur: () => void;
	render: (params: ValueRenderMethodParams<TFieldValues, TName, TValue>) => React.ReactElement;
};

function RHFValueFieldWrapper<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues,
	TValue = unknown
>(props: RHFValueFieldWrapperProps<TFieldValues, TName, TTransformedValues, TValue>) {
	const { name, control, defaultValue, rules, onChange, onBlur, render } = props;

	const { field, fieldState } = useController<TFieldValues, TName, TTransformedValues>({
		name,
		control,
		defaultValue,
		rules,
	});

	const onFieldChange = React.useCallback((value: TValue) => {
		field.onChange(value);
		onChange?.(name);
	}, [field, name, onChange]);

	const onFieldBlur = React.useCallback(() => {
		field.onBlur();
		onBlur();
	}, [field, onBlur]);

	const error: Nullable<CellError> = React.useMemo(() => {
		if (fieldState.error?.message) {
			return {
				message: fieldState.error.message,
				type: isErrorType(fieldState.error.type)
					? fieldState.error.type
					: 'other',
			};
		}
		return null;
	}, [fieldState]);

	return render({
		field,
		error,
		onFieldChange,
		onFieldBlur,
	});
}

export default React.memo(RHFValueFieldWrapper) as typeof RHFValueFieldWrapper;
